import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { UserPlus, ArrowRight } from "lucide-react";

const INDUSTRIES = [
  "SaaS / Software", "Professional Services", "Manufacturing", "Retail / E-Commerce",
  "Healthcare", "Financial Services", "Education", "Real Estate", "Media / Entertainment", "Other",
];

export default function RegisterPage() {
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "", industry: "" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.name) {
      toast.error("Please fill in required fields");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const update = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center p-12">
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">U</span>
            </div>
            <span className="text-white font-heading font-bold text-xl tracking-tight">USG</span>
          </div>
          <h1 className="text-4xl font-heading font-bold text-white mb-4 tracking-tight leading-tight">
            Your AI Strategy<br/>Starts Here.
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            Join hundreds of leadership teams mapping their business workflows and making strategic AI adoption decisions.
          </p>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">U</span>
            </div>
            <span className="font-heading font-bold text-xl tracking-tight text-slate-900">USG</span>
          </div>

          <h2 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">Create Account</h2>
          <p className="text-slate-500 mt-2 mb-8">Get started with the Unstoppable Company Game.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Full Name *</Label>
              <Input
                data-testid="register-name-input"
                placeholder="Jane Smith"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="h-11 bg-white border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Email *</Label>
              <Input
                type="email"
                data-testid="register-email-input"
                placeholder="you@company.com"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="h-11 bg-white border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Password *</Label>
              <Input
                type="password"
                data-testid="register-password-input"
                placeholder="Create a strong password"
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                className="h-11 bg-white border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Company Name</Label>
              <Input
                data-testid="register-company-input"
                placeholder="Acme Inc."
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                className="h-11 bg-white border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Industry</Label>
              <Select value={form.industry} onValueChange={(v) => update("industry", v)}>
                <SelectTrigger data-testid="register-industry-select" className="h-11 bg-white border-slate-200">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((ind) => (
                    <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg mt-2 active:scale-[0.98]"
            >
              {loading ? "Creating account..." : (
                <span className="flex items-center justify-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Create Account
                </span>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="text-indigo-600 font-medium hover:text-indigo-500" data-testid="login-link">
              Sign in <ArrowRight className="inline w-3 h-3" />
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
